'use client'

import { useAppRouter } from '@/shared/lib/router'
import type { FieldValues, UseFormReturn } from 'react-hook-form'

import { useQueryClient } from '@tanstack/react-query'

import { useDataProviderContext } from '@/shared/contexts/DataProvider'
import {
  useResourceContext,
  type CustomAction,
  type RecordType
} from '@/shared/contexts/Resource'
import { showErrorAlert } from '@/shared/lib/ui'

type UseSubmitHandlerProps<TRecord extends RecordType = RecordType> = {
  methods: UseFormReturn<FieldValues>
  customFormAction?: CustomAction
  onError?: (error: unknown) => void
  beforeSave?: (data: TRecord) => Promise<TRecord>
  onSave?: (record: TRecord, goBack: () => void) => void
  onSubmit?: (data: TRecord) => Promise<any>
  resetOnSave?: boolean
  nestedFields?: string[]
  referenceFields?: string[]
}

type ErrorMap = Record<string, string[] | string>

export function useSubmitHandler<TRecord extends RecordType = RecordType>({
  methods,
  beforeSave = async data => data,
  customFormAction,
  onError,
  onSave,
  onSubmit,
  resetOnSave
}: UseSubmitHandlerProps<TRecord>) {
  const { dataProvider } = useDataProviderContext()
  const queryClient = useQueryClient()
  const router = useAppRouter()
  const {
    id,
    record,
    setSavedRecord,
    isEdit,
    setIsSubmitting,
    config: { apiBasePath, customAction }
  } = useResourceContext<TRecord>()

  const currentCustomAction = customFormAction || customAction

  const handleCreate = (data: TRecord) => dataProvider.create(apiBasePath, data)
  const handleUpdate = (data: TRecord) => dataProvider.update(apiBasePath, id, data)
  const handleCustomAction = (data: TRecord) =>
    dataProvider.customAction(
      apiBasePath,
      id,
      typeof currentCustomAction === 'string'
        ? currentCustomAction
        : currentCustomAction?.name,
      typeof currentCustomAction === 'string'
        ? 'post'
        : currentCustomAction?.method ?? 'post',
      data
    )

  const setErrors = (data: TRecord, errors: ErrorMap) => {
    Object.entries(errors).forEach(([key, value]) => {
      if (!Object.prototype.hasOwnProperty.call(data, key)) return

      methods.setError(key, {
        type: 'manual',
        message: Array.isArray(value) ? value.join(', ') : value
      })
    })
  }

  const handleErrors = async (
    data: TRecord,
    errors: any,
    errorMessage?: string
  ) => {
    const responseErrors = errors?.response?.data?.errors as ErrorMap | undefined

    if (responseErrors && typeof responseErrors === 'object') {
      setErrors(data, responseErrors)
    } else {
      await showErrorAlert(errorMessage || errors)
    }

    onError?.(errors)
  }

  const handleSubmit = async (data: TRecord) => {
    let actionHandler = isEdit ? handleUpdate : handleCreate
    if (onSubmit) actionHandler = onSubmit
    if (currentCustomAction) actionHandler = handleCustomAction

    setIsSubmitting(true)

    try {
      const newData = await beforeSave(data)
      const saveResponse = await actionHandler(newData)
      const savedRecord = {
        ...(isEdit ? (record ?? {}) : {}),
        ...newData,
        ...(saveResponse?.data?.record ?? {})
      } as TRecord
      const errors = saveResponse?.data?.errors
      const errorMessage = saveResponse?.data?.error_message

      setSavedRecord(savedRecord)
      await queryClient.invalidateQueries({ queryKey: [apiBasePath] })

      if (errors || errorMessage) {
        await handleErrors(data, saveResponse, errorMessage)
      } else {
        if (resetOnSave) methods.reset()

        if (onSave) onSave(savedRecord, () => router.back())
        else router.back()
      }
    } catch (error) {
      await handleErrors(data, error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return { handleSubmit }
}
